import { useIsMutating } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { SpinnerCircular } from "spinners-react";
import { useGetFieldLabel } from "../../contexts/LanguageContext";
import { useToggle } from "../../hooks/common";
import {
  TCreateFormPayload,
  useAllowedFields,
  useAlreadyCreatedForms,
  useServiceTypes,
} from "../../hooks/forms.hooks";
import Input from "../CommonComponents/Input/Input";
import Select from "../CommonComponents/Select/Select";
import Switch from "../CommonComponents/Switch/Switch";
import { ErrorToast } from "../toastMessage";
import { formTypes } from "./FormConfigurations.const";
import PreviewDialog from "./PreviewDialog";

type FormConfigFormProps = {
  ctx: "add" | "edit";
  defaultEnabledDocuments?: number[];
  defaultEnabledFields?: number[];
  defaultValues: {
    name: string;
    type: string;
    serviceName: string;
    active: boolean;
    isNewForm: boolean;
  };
  onSubmit: (_: TCreateFormPayload) => unknown;
};

export default function FormConfigForm({
  ctx,
  defaultEnabledDocuments,
  defaultEnabledFields,
  defaultValues,
  onSubmit,
}: FormConfigFormProps) {
  const convert = useGetFieldLabel();
  const [preview, togglePreview] = useToggle();
  const { data: serviceTypesResult } = useServiceTypes();
  const { data: result } = useAlreadyCreatedForms();
  const isMutating = useIsMutating() > 0;
  const [fieldsEnabled, setEnabledFields] = useState<number[]>(
    defaultEnabledFields || []
  );
  const [documentsEnabled, setEnabledDocuments] = useState<number[]>(
    defaultEnabledDocuments || []
  );
  const { register, handleSubmit, formState, control } = useForm({
    defaultValues,
  });
  const typeValue = useWatch({
    control: control,
    name: "type",
    defaultValue: defaultValues.type,
  });
  const serviceNameValue = useWatch({
    control: control,
    name: "serviceName",
    defaultValue: defaultValues.serviceName,
  });
  const {
    isError,
    isFetching,
    data: allowedFields,
  } = useAllowedFields({ serviceName: serviceNameValue, type: typeValue });
  const navigate = useNavigate();

  const serviceTypes = serviceTypesResult?.data || [];
  const created = new Set((result || [])?.map((e) => e.type));
  const createdServices = new Set((result || [])?.map((e) => e.serviceName));
  const isEnabled =
    !!typeValue && (typeValue === "Service Form" ? !!serviceNameValue : true);

  useEffect(() => {
    if (isFetching || ctx !== "add") return;
    setEnabledFields((e) => {
      if (e.length) return e;
      return (allowedFields?.fields || [])
        .filter((e) => e.active)
        .map((e) => e.id);
    });
    setEnabledDocuments((e) => {
      if (e?.length) return e;
      return (allowedFields?.documents || [])
        .filter((e) => e.active)
        .map((e) => e.document_config_id);
    });
  }, [isFetching, allowedFields, ctx]);

  let toRender = null;

  const toggleSelectedField = (id: number) =>
    setEnabledFields((p) =>
      p.includes(id) ? p.filter((e) => e !== id) : [...p, id]
    );

  const toggleSelectedDocument = (id: number) =>
    setEnabledDocuments((p) =>
      p.includes(id) ? p.filter((e) => e !== id) : [...p, id]
    );

  const reset = () => {
    setEnabledDocuments([]);
    setEnabledFields([]);
  };
console.log("each.id", allowedFields?.fields)
  if (isError || isFetching) {
    toRender = (
      <div className="flex justify-center items-center p-4">
        {isFetching ? (
          <SpinnerCircular />
        ) : (
          <p>Something went wrong while fetching info!</p>
        )}
      </div>
    );
  } else if (
    isEnabled &&
    !allowedFields?.documents?.length &&
    !allowedFields?.fields?.length
  ) {
    toRender = (
      <div className="flex justify-center items-center p-4">
        <p>No config found for selected form type!</p>
      </div>
    );
  } else if (
    allowedFields?.documents?.length ||
    allowedFields?.fields?.length
  ) {
    let i = 0;
    toRender = (
      <div>
        <table className="table-fixed p-0 m-0 w-full">
          <tbody>
            {(allowedFields?.fields || [])?.map((each) => (
              <tr
                key={`field-${each.id}`}
                className={i++ % 2 ? "bg-white" : "bg-gray-200"}
              >
                <td className="w-[10%] text-center text-xs p-2">
                  <input
                    type="checkbox"
                    checked={fieldsEnabled.includes(each.id)}
                    onChange={() => toggleSelectedField(each.id)}
                    disabled={each.id === 8}
                  />
                </td>
                <td className="w-[10%] text-center text-xs p-2">{i}</td>
                <td className="w-[80%] text p-2 text-xs">{convert(each)}</td>
              </tr>
            ))}
            {(allowedFields?.documents || [])?.map((e) => (
              <tr
                key={`document-${e.document_config_id}`}
                className={i++ % 2 ? "bg-white" : "bg-gray-200"}
              >
                <td className="w-[10%] text-center text-xs p-2">
                  <input
                    type="checkbox"
                    checked={documentsEnabled.includes(e.document_config_id)}
                    onChange={() =>
                      toggleSelectedDocument(e.document_config_id)
                    }
                    disabled={e.document_config_id === 1 || e.document_config_id === 2}
                  />
                </td>
                <td className="w-[10%] text-center text-xs p-2">{i}</td>
                <td className="w-[80%] text p-2 text-xs">{e.document_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <form
        noValidate
        onSubmit={handleSubmit((data) => {
          if (!documentsEnabled.length && !fieldsEnabled.length) {
            if (data.serviceName !== "Gunthewari") {
              ErrorToast("Please at least select one field!");
              return;
            }
          }
          onSubmit({ ...data, documentsEnabled, fieldsEnabled });
        })}
        // className="bg-white rounded-3xl overflow-auto p-4 md:p-6"
        className="bg-white rounded-xl border border-gray-300 overflow-auto shadow-lg p-4 md:p-6"
      >
        <div className="flex-wrap md:flex-nowrap gap-4 flex justify-between items-center">
          <div className="flex items-center space-x-6 mr-auto">
            <p className="shrink-0 font-bold text-grey-600">Name of Form</p>
            <Input
              placeholder="Form Name"
              containerClass="grow"
              {...register("name", { required: "* Required" })}
              error={!!formState?.errors?.name}
              helperText={formState?.errors?.name?.message?.toString()}
            />
          </div>
          <div className="flex space-x-4 items-center">
            <label
              htmlFor="active"
              className="shrink-0 font-bold text-grey-600"
            >
              Activate
            </label>
            <Switch {...register("active")} id="active" />
          </div>
          {(!!fieldsEnabled?.length || !!documentsEnabled?.length) && (
            <button
              type="button"
              onClick={togglePreview}
              className="py-2 px-6 font-semibold bg-primary text-white rounded-lg"
            >
              <i className="text-white fa fa-eye"></i>
              Preview
            </button>
          )}
        </div>
        <div className="flex-wrap md:flex-nowrap gap-4 flex justify-between  items-end">
          {defaultValues.isNewForm ? (
            <div className="pb-2">
              <p className="font-bold text-black pb-5">Form Type</p>
              <p className="pl-3">Service Form</p>
            </div>
          ) : (
            <div className="basis-60 grow md:grow-0 mt-6">
              <Select
                label="Form Type"
                disabled={ctx === "edit"}
                error={!!formState?.errors?.type}
                helperText={formState?.errors?.type?.message?.toString()}
                {...register("type", {
                  required: "* Required",
                  onChange: reset,
                })}
              >
                {formTypes.map((e) => (
                  <option
                    key={e.value}
                    value={e.value}
                    disabled={
                      e.value !== "Service Form" && created.has(e.value)
                    }
                  >
                    {e.label}
                  </option>
                ))}
              </Select>
            </div>
          )}
          {typeValue === "Service Form" && (
            <div className="basis-60 grow md:grow-0 mt-6">
              <Select
                label="Name of Service"
                disabled={ctx === "edit"}
                error={!!formState?.errors?.serviceName}
                helperText={formState?.errors?.serviceName?.message?.toString()}
                {...register("serviceName", {
                  required: "* Required",
                  onChange: reset,
                })}
              >
                {serviceTypes.map((e) => (
                  <option
                    value={e.name}
                    key={e.id}
                    disabled={createdServices.has(e.name)}
                  >
                    {e.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <div className="grow md:grow-0 flex gap-5 shrink-0">
            <button
              type="submit"
              disabled={isMutating}
              className="py-2 px-6 font-semibold bg-primary text-white rounded-lg grow md:grow-0"
            >
              Publish
            </button>
            <button
              // type="close"

              onClick={() => navigate("/form-configurations")}
              className="py-2 px-6 font-semibold bg-black text-white rounded-lg grow md:grow-0"
            >
              Close
            </button>
          </div>
        </div>
        {isEnabled && <hr className="my-6" />}
        {toRender}
        {preview && (
          <PreviewDialog
            onClose={togglePreview}
            open
            selectedDocuments={
              allowedFields?.documents?.filter((e) =>
                documentsEnabled.includes(e.document_config_id)
              ) || []
            }
            selectedFields={
              allowedFields?.fields?.filter((e) =>
                fieldsEnabled.includes(e.id)
              ) || []
            }
          ></PreviewDialog>
        )}
      </form>
    </div>
  );
}
